import { useEffect, useState, useContext } from "react";
import { CircularProgress } from "@mui/material";
import { Divider } from '@material-ui/core';
import { DateTime } from 'luxon';
import { useNotification } from '../../../../../context/NotificationContext';

import { useGetRequestActivityQuery } from "../../../../../services/requestActivityApi";
import { useGetUniqueRequestersQuery } from "../../../../../services/swarmsApi";
import { useGetUniqueBuyersQuery } from "../../../../../services/swarmsApi";
import SignalREncorehiveContext from "@/context/signalREncorehive";

const SwarmsMetricsDisplay = () => {

  const { signalREncorehive } = useContext(SignalREncorehiveContext);
  const { showNotification } = useNotification();

  const nowUTC = DateTime.now().toUTC();

  const format = 'yyyy/MM/dd HH:mm:ss';

  let endDateUniqueRequesters = nowUTC.minus({ hours: nowUTC.minute < 30 ? 1 : 0 }).set({
    minute: nowUTC.minute < 30 ? 59 : 29,
    second: 59,
    millisecond: 99
  });

  let startDateUniqueRequesters = nowUTC.minus({ hours: nowUTC.minute < 30 ? 1 : 0 }).set({
    minute: nowUTC.minute < 30 ? 30 : 0,
    second: 0,
    millisecond: 0
  });
  
  startDateUniqueRequesters = startDateUniqueRequesters.toFormat(format);
  endDateUniqueRequesters = endDateUniqueRequesters.toFormat(format);

  let startDateUniqueBuyers = nowUTC.startOf('day').set({ second: 0, millisecond: 0 }); 
  let endDateUniqueBuyers = nowUTC.endOf('day').set({ second: 59, millisecond: 999 }); 

  startDateUniqueBuyers = startDateUniqueBuyers.toFormat(format);
  endDateUniqueBuyers = endDateUniqueBuyers.toFormat(format);

  const [totalRequestActivity, setTotalRequestActivity] = useState(null);
  const [uniqueRequesters, setUniqueRequesters] = useState(null);
  const [totalUniqueBuyers, setUniqueBuyers] = useState(null);

  const {data: dataRequestActivity, isLoading: getRequestActivityIsLoading} = useGetRequestActivityQuery();
  const {data: dataUniqueRequesters, isLoading: getUniqueRequestersIsLoading} = useGetUniqueRequestersQuery({ startDate: startDateUniqueRequesters, endDate: endDateUniqueRequesters });
  const {data: dataUniqueBuyers, isLoading: getUniqueBuyersIsLoading, refetch: refetchUniqueBuyers} = useGetUniqueBuyersQuery({ startDate: startDateUniqueBuyers, endDate: endDateUniqueBuyers });

  const isLoading = getRequestActivityIsLoading || getUniqueRequestersIsLoading || getUniqueBuyersIsLoading;
  
  useEffect(() => {
    if (!dataRequestActivity) {
      setTotalRequestActivity({});
      return;
    }  
    const totalRequestActivity = dataRequestActivity.swarmsResume.reduce(
      (totalRequestActivity, requestActivity) => {
        totalRequestActivity.completed += requestActivity.completed?.tickets || 0;
        totalRequestActivity.rejected += requestActivity.rejected?.tickets || 0;
        totalRequestActivity.lost += requestActivity.lost?.tickets || 0;
        // totalRequestActivity.accepted += requestActivity.accepted?.tickets || 0;
        return totalRequestActivity;
      },
      // { completed: 0, rejected: 0, lost: 0, accepted: 0 }
      { completed: 0, rejected: 0, lost: 0 }
    );
    setTotalRequestActivity(totalRequestActivity);
  }, [dataRequestActivity]);

  useEffect(() => {
    if (!dataUniqueRequesters) {
      setUniqueRequesters(null);
      return;
    }
    const uniqueRequesters = dataUniqueRequesters[0]?.uniqueRequestersCount || 0;
    setUniqueRequesters(uniqueRequesters);
  }, [dataUniqueRequesters]);

  useEffect(() => {
    if (!dataUniqueBuyers) {
      setUniqueBuyers(null);
      return;
    }
    const uniqueBuyers = dataUniqueBuyers[dataUniqueBuyers.length - 1]?.uniqueBuyersCount || 0;
    setUniqueBuyers(uniqueBuyers);
  }, [dataUniqueBuyers]);

  useEffect(() => {

    const checkForRefetch = () => {
      
      const now = DateTime.now().toUTC();
      const minutes = now.minute;

      if (minutes === 0 || minutes === 30) {
        refetchUniqueBuyers(); 
      }
    };

    const intervalId = setInterval(checkForRefetch, 60000); 

    return () => clearInterval(intervalId);
  }, [refetchUniqueBuyers]);

  useEffect(() => {
    if (!isLoading && (!dataRequestActivity || !dataUniqueRequesters || !dataUniqueBuyers)) {
       showNotification(
          <>
            <div>Metrics unavailable, please refresh</div>
            <div>Error retrieving data for: </div>
            {!dataRequestActivity && <div>Today Total Activity</div>}
            {!dataUniqueRequesters && <div>Currently Active Buyers</div>}
            {!dataUniqueBuyers && <div>Total Buyers Today</div>}
          </>,
          'error'
       );
    }
 }, [isLoading]);

  useEffect(() => {
    // meybe we can impove this to avoid doing a request every time we want to update the metrics
    signalREncorehive?.on('CompletedSwarmBuyRequest', (data) => {
      console.log("[signalR] CompletedSwarmBuyRequest", data);
      setTotalRequestActivity(prev => ({
        ...prev,
        completed: (prev.completed || 0) + data.quantity
      }));
    })

    signalREncorehive?.on('RejectedSwarmBuyRequest', (data) => {
      console.log("[signalR] RejectedSwarmBuyRequest", data);
      setTotalRequestActivity(prev => ({
        ...prev,
        rejected: (prev.rejected || 0) + data.quantity
      }));
    })

    signalREncorehive?.on('LostSwarmBuyRequest', (data) => {
      console.log("[signalR] LostSwarmBuyRequest", data);
      setTotalRequestActivity(prev => ({
        ...prev,
        lost: (prev.lost || 0) + data.quantity
      }));
    })
    
  }, [signalREncorehive])

  return (

    <div>

      {isLoading ? (
        <div style={{ fontWeight: 600, display: "flex" }}> <CircularProgress size={25} color="inherit" /> </div>
      ) : (
        <div style={{ display: 'flex', fontSize: '0.95em', padding: '10px', flexWrap: 'wrap', justifyContent:'space-between' }}>

          <div style={{ display: 'flex', gap: '0.5em', fontWeight: 600 }}>Currently Active Buyers: {uniqueRequesters || 0}</div>

          <Divider orientation="vertical" variant="middle" flexItem style={{ margin: '0 5px', background: '#2A4F10',  }} />

          <div style={{ display: 'flex', gap: '0.5em', fontWeight: 600 }}>Total Buyers Today: {totalUniqueBuyers || 0}</div>
          
          <Divider orientation="vertical" variant="middle" flexItem style={{ margin: '0 5px', background: '#2A4F10' }} />

          {totalRequestActivity && (
            <div style={{ display: 'flex', gap: '0.5em' }}>
              <div style={{ fontWeight: 600 }}>Today Total Activity</div>
              <div style={{ display: 'flex', gap: '0.5em' }}>
              <div>Bought: <span style={{ fontWeight: 600 }}>{totalRequestActivity.completed || 0}</span></div>
              <div>Passed: <span style={{ fontWeight: 600 }}>{totalRequestActivity.rejected || 0}</span></div>
              <div>Lost: <span style={{ fontWeight: 600 }}>{totalRequestActivity.lost || 0}</span></div>
              {/* <div>Pending: <span style={{ fontWeight: 600 }}>{totalRequestActivity.accepted || 0}</span></div> */}
              </div>
            </div>
          )}

        </div>
      )}

    </div>

  );
};

export default SwarmsMetricsDisplay;
